import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ChangePasswordService } from '@services/auth/changePassword.service';
import { AlertsService } from '@services/AlertsSystem/alerts.service';
import { ThemeService } from '@services/theme/theme.service';
import { TranslationsService } from '@services/translate/translations.service';

@Component({
  selector: 'features-auth-change-password-page',
  imports: [MatIconModule, ReactiveFormsModule, ProgressSpinnerModule, ToastModule],
  templateUrl: './changePasswordPage.component.html',
  styleUrl: './changePasswordPage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
/**
 * Página de cambio de contraseña para usuarios autenticados.
 *
 * Accesible desde el perfil de usuario en cualquier rol. Permite al usuario
 * establecer una nueva contraseña cumpliendo los requisitos de seguridad
 * (mínimo 8 caracteres, mayúsculas, minúsculas, número y carácter especial).
 * Tras el éxito redirige al perfil del rol correspondiente.
 */
export class ChangePasswordPageComponent {
  private readonly _fb                    = inject(FormBuilder);
  private readonly _router                = inject(Router);
  private readonly _changePasswordService = inject(ChangePasswordService);
  private readonly _alertService          = inject(AlertsService);
  private readonly _theme                 = inject(ThemeService);
  private readonly _translations          = inject(TranslationsService);

  /** Color primario del tema, reactivo a cambios desde el backend. */
  readonly primaryColor = this._theme.primaryColor;
  /** Controla la visibilidad del campo de contraseña (texto plano vs oculto). */
  readonly showPassword = signal(false);
  /** Indica que hay una petición HTTP en curso al enviar el formulario. */
  readonly loading      = signal(false);
  /** Mensaje de error a mostrar bajo el formulario cuando el cambio falla. */
  readonly errorMessage = signal<string | null>(null);

  // ── Signals de sección ───────────────────────────────────────────────────
  private readonly _profileSignal = this._translations.sectionSignal('setting_my_profile');
  private readonly _formsSignal   = this._translations.sectionSignal('forms');
  private readonly _btnSignal     = this._translations.sectionSignal('button');
  private readonly _msgSignal     = this._translations.sectionSignal('message_system');

  /** Helper de traducción para la sección `setting_my_profile`. */
  readonly tProfile = (key: string) => this._profileSignal()?.[key] ?? this._profileSignal()?.gene_code_tran_code_keys?.[key];
  /** Helper de traducción para la sección `forms`. */
  readonly tForms   = (key: string) => this._formsSignal()?.[key]   ?? this._formsSignal()?.gene_code_tran_code_keys?.[key];
  /** Helper de traducción para la sección `button`. */
  readonly tBtn     = (key: string) => this._btnSignal()?.[key]     ?? this._btnSignal()?.gene_code_tran_code_keys?.[key];
  /** Helper de traducción para la sección `message_system`. */
  readonly tMsg     = (key: string) => this._msgSignal()?.[key]     ?? this._msgSignal()?.gene_code_tran_code_keys?.[key];

  readonly form = this._fb.group(
    {
      newPassword:     ['', [Validators.required, Validators.minLength(8), this.hasBothCase.bind(this), this.hasNumber.bind(this), this.hasSpecialChar.bind(this)]],
      confirmPassword: ['', [Validators.required]],
    },
    { validators: this.passwordsMatch.bind(this) },
  );

  private passwordsMatch(group: AbstractControl): ValidationErrors | null {
    const pwd     = group.get('newPassword')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return pwd && confirm && pwd !== confirm ? { mismatch: true } : null;
  }

  private hasBothCase(control: AbstractControl): ValidationErrors | null {
    const v = control.value as string;
    return /[A-Z]/.test(v) && /[a-z]/.test(v) ? null : { bothCase: true };
  }

  private hasNumber(control: AbstractControl): ValidationErrors | null {
    return /\d/.test(control.value as string) ? null : { hasNumber: true };
  }

  private hasSpecialChar(control: AbstractControl): ValidationErrors | null {
    return /[\W_]/.test(control.value as string) ? null : { hasSpecialChar: true };
  }

  /** Alterna la visibilidad del campo de contraseña entre texto plano y oculto. */
  togglePasswordVisibility(): void {
    this.showPassword.update(v => !v);
  }

  /**
   * Envía la nueva contraseña al backend mediante `ChangePasswordService`.
   * Muestra toast de éxito y redirige al perfil del usuario tras 1.5 segundos.
   */
  async submit(): Promise<void> {
    // El botón escucha `click` y `keydown.enter`: con Enter llegaban los dos eventos y se
    // enviaba la petición dos veces.
    if (this.form.invalid || this.loading()) return;

    this.loading.set(true);
    this.errorMessage.set(null);

    const { newPassword, confirmPassword } = this.form.getRawValue();

    try {
      await this._changePasswordService.changePassword({ newPassword, confirmPassword });
      this._alertService.showSuccess(
        this.tProfile('change_password_success_title'),
        this.tMsg('update_data_message'),
      );
      setTimeout(() => this.goBack(), 1500);
    } catch {
      this.errorMessage.set(this.tProfile('change_password_error'));
      this.loading.set(false);
    }
  }

  /** Redirige al perfil del usuario según su rol (`/configuration/my-profile`, `/executor/my-profile`, etc.). */
  goBack(): void {
    const role    = localStorage.getItem('role')?.toLowerCase();
    const urlRole = role === 'administrator' ? 'configuration' : role;
    void this._router.navigate([`/${urlRole}/my-profile`]);
  }
}
